import { BaseHandler } from "./base.handler";
import type { WebSocketResponse } from "@/types/generated-types";
import { RESULT } from "@/types";
import { useAppStore } from "@/stores";
import { WebSocketService } from "../websocket.service";

/**
 * Generic subscription handler capturing the shared stream lifecycle so a new
 * stream is a ~10-line subclass, mirroring the backend's "one subscription =
 * one declarative unit".
 *
 * The wire flow for every stream:
 *   send subscribe:<name>  →  ack (result ok, NO data)  →  initial page
 *   (subscribe:<name> WITH data)  →  update:<name> (periodic / event) …
 *
 * A subclass supplies the two message types and the store bindings; the base
 * owns the auth/loading gate, the ack-then-page handshake, and error reset.
 */
export abstract class StreamHandler<TData> extends BaseHandler {
  /** e.g. "subscribe:campaigns". */
  protected abstract readonly subscribeType: string;
  /** e.g. "update:campaigns". */
  protected abstract readonly updateType: string;

  /** True when already subscribed or a subscribe is in flight (avoids double-subscribe). */
  protected abstract isBusy(): boolean;
  /** Flip the stream's loading flag. */
  protected abstract setLoading(loading: boolean): void;
  /** Mark subscribed (implementations also clear loading). */
  protected abstract setSubscribed(subscribed: boolean): void;
  /** Drop the stream's data + flags (used on unsubscribe). */
  protected abstract clear(): void;
  /** Apply a payload page. `initial` = the subscribe page (vs. a later update). */
  protected abstract apply(data: TData, initial: boolean): void;

  /** Handshake state, so ack-vs-page never rides on `data == null` alone. */
  private acked = false;
  private initialPageApplied = false;

  subscribe(): void {
    if (!useAppStore.getState().authenticated) {
      this.logger.warn(`Cannot subscribe (${this.subscribeType}): not authenticated`);
      return;
    }
    if (this.isBusy()) {
      this.logger.warn(`Already subscribed or loading (${this.subscribeType})`);
      return;
    }
    this.acked = false;
    this.initialPageApplied = false;
    this.setLoading(true);
    WebSocketService.getInstance().send(this.subscribeType, {});
  }

  unsubscribe(): void {
    this.acked = false;
    this.initialPageApplied = false;
    this.clear();
  }

  canHandle(messageType: string): boolean {
    return messageType === this.subscribeType || messageType === this.updateType;
  }

  async handle(message: WebSocketResponse): Promise<void> {
    if (!this.validateMessage(message)) return;

    if (message.type === this.subscribeType) {
      if (message.result !== RESULT.OK) {
        this.logger.error(`Subscription failed (${this.subscribeType})`, message);
        this.acked = false;
        this.initialPageApplied = false;
        this.setSubscribed(false);
        this.setLoading(false);
        return;
      }
      // Wire flow: ack (no data) → initial page (subscribe WITH data) → updates.
      // Don't infer ack-vs-page from `data == null` alone: an empty initial page
      // sent as null/omitted would otherwise read as a second ack and wedge the
      // stream in `loading` forever. A page carrying data acks implicitly (covers
      // a backend that sends data on the first message).
      if (message.data != null) {
        this.acked = true;
        this.initialPageApplied = true;
        this.setSubscribed(true);
        this.apply(message.data as TData, true);
        return;
      }
      if (!this.acked) {
        this.acked = true; // pure ack; keep loading, await the page
        return;
      }
      if (!this.initialPageApplied) {
        this.initialPageApplied = true; // empty initial page arrived as null
        this.setSubscribed(true);
        this.apply([] as unknown as TData, true);
      }
      return;
    }

    if (message.type === this.updateType) {
      if (message.result === RESULT.OK && message.data != null) {
        this.apply(message.data as TData, false);
      }
    }
  }
}
